import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { asset } from './asset';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';

const navItems = [
  ['nav.home', '/#home'],
  ['nav.about', '/#about'],
  ['nav.system', '/#system'],
  ['nav.sensors', '/#sensors'],
  ['nav.value', '/#business-value'],
  ['nav.compliance', '/#compliance'],
  ['nav.contact', '/contact'],
];

function NavigationHeader() {
  const [isOpen, setIsOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const { t } = useTranslation();
  const langMenuRef = useRef(null);

  useEffect(() => {
    function onDocMouseDown(event) {
      if (!langMenuRef.current) return;
      if (langMenuRef.current.contains(event.target)) return;
      setIsLangOpen(false);
    }
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, []);

  const currentFlag = i18n.language === 'ur' ? 'pakistan-flag.png' : 'us-flag.png';

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-slate-200 bg-white/95 backdrop-blur-md">
      <div className="flex items-center justify-between mx-auto w-full sm:max-w-[80%] max-w-[90%] py-4 lg:py-4">
        <Link to="/#home" aria-label="Go to top" className="block" onClick={() => setIsOpen(false)}>
          <img
            src={asset('Poultri-logo.png')}
            alt="Poultri by AAZIRA"
            className="w-[150px] object-contain object-left "
          />
        </Link>

        <nav className="hidden items-center gap-4 xl:flex" aria-label="Main navigation">
          {navItems.map(([labelKey, href]) => (
            <Link
              key={href}
              to={href}
              className="text-[11px] font-extrabold uppercase text-slate-600 transition hover:text-emerald-500"
            >
              {t(labelKey)}
            </Link>
          ))}
          <div className="ml-2 h-6 w-px bg-slate-200" />
          <div className="relative" ref={langMenuRef}>
            <button
              type="button"
              onClick={() => setIsLangOpen((v) => !v)}
              className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full border border-slate-200 transition hover:border-slate-300"
              aria-label={t('nav.language')}
              aria-expanded={isLangOpen}
              aria-haspopup="menu"
              title={t('nav.language')}
            >
              <img src={asset(currentFlag)} alt="" className="h-full w-full object-cover" />
            </button>

            {isLangOpen && (
              <div
                role="menu"
                className="absolute right-0 mt-2 grid w-44 gap-1 rounded border border-slate-200 bg-white p-2 shadow-xl"
              >
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    i18n.changeLanguage('en');
                    setIsLangOpen(false);
                  }}
                  className={`flex items-center gap-3 rounded px-2 py-2 text-sm font-bold ${
                    i18n.language === 'en' ? 'bg-emerald-50 text-emerald-700' : 'text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  <img src={asset('us-flag.png')} alt="" className="h-6 w-6 rounded-full object-cover" />
                  {t('nav.english')}
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    i18n.changeLanguage('ur');
                    setIsLangOpen(false);
                  }}
                  className={`flex items-center gap-3 rounded px-2 py-2 text-sm font-bold ${
                    i18n.language === 'ur' ? 'bg-emerald-50 text-emerald-700' : 'text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  <img src={asset('pakistan-flag.png')} alt="" className="h-6 w-6 rounded-full object-cover" />
                  {t('nav.urdu')}
                </button>
              </div>
            )}
          </div>
        </nav>

        <button
          type="button"
          className="flex h-11 w-11 items-center justify-center rounded border border-slate-200 text-[#0b1020] xl:hidden"
          aria-label="Toggle navigation menu"
          aria-expanded={isOpen}
          aria-controls="mobile-homepage-menu"
          onClick={() => setIsOpen((current) => !current)}
        >
          <span className="flex w-5 flex-col gap-1.5" aria-hidden="true">
            <span className="h-0.5 rounded bg-current" />
            <span className="h-0.5 rounded bg-current" />
            <span className="h-0.5 rounded bg-current" />
          </span>
        </button>
      </div>

      {isOpen && (
        <nav
          id="mobile-homepage-menu"
          className="absolute left-5 right-5 top-full mt-3 grid gap-1 rounded border border-slate-200 bg-white p-3 shadow-2xl sm:left-8 sm:right-8 lg:left-12 lg:right-12 xl:hidden"
          aria-label="Mobile navigation"
        >
          {navItems.map(([labelKey, href]) => (
            <Link
              key={href}
              to={href}
              className="rounded px-3 py-3 text-sm font-extrabold uppercase text-slate-700 hover:bg-emerald-50 hover:text-emerald-600"
              onClick={() => setIsOpen(false)}
            >
              {t(labelKey)}
            </Link>
          ))}
          <div className="my-1 h-px w-full bg-slate-200" />
          <div className="grid gap-2 p-2">
            <p className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-slate-500">{t('nav.language')}</p>
            <div className="grid gap-2">
              <button
                type="button"
                className={`flex items-center gap-3 rounded border px-3 py-2 text-sm font-extrabold uppercase ${
                  i18n.language === 'en' ? 'border-emerald-500 text-emerald-700' : 'border-slate-200 text-slate-700'
                }`}
                onClick={() => i18n.changeLanguage('en')}
              >
                <img src={asset('us-flag.png')} alt="" className="h-7 w-7 rounded-full object-cover" />
                {t('nav.english')}
              </button>
              <button
                type="button"
                className={`flex items-center gap-3 rounded border px-3 py-2 text-sm font-extrabold uppercase ${
                  i18n.language === 'ur' ? 'border-emerald-500 text-emerald-700' : 'border-slate-200 text-slate-700'
                }`}
                onClick={() => i18n.changeLanguage('ur')}
              >
                <img src={asset('pakistan-flag.png')} alt="" className="h-7 w-7 rounded-full object-cover" />
                {t('nav.urdu')}
              </button>
            </div>
          </div>
        </nav>
      )}
    </header>
  );
}

export default NavigationHeader;
